(() => {
  if (customElements.get('pj-spring-carnival')) return;
  class PJSpringCarnival extends HTMLElement {
    connectedCallback() { requestAnimationFrame(() => { if (this.isConnected && !this.initialised) this.init(); }); }
    disconnectedCallback() {
      this.abort?.abort(); this.reveals?.disconnect(); this.resize?.disconnect(); this.videoObserver?.disconnect();
      this.carouselCleanup?.forEach(cleanup => cleanup());
      cancelAnimationFrame(this.scrollFrame); this.initialised = false;
    }
    init() {
      this.initialised = true; this.abort = new AbortController();
      const signal = this.abort.signal, on = (el, event, fn, options = {}) => el.addEventListener(event, fn, { ...options, signal });
      const reduced = matchMedia('(prefers-reduced-motion: reduce)'); this.reduced = reduced;
      const animationsEnabled = () => this.dataset.motion === 'true' && document.querySelector('[data-pj-navigation-root]')?.dataset.motion !== 'false';
      if (!reduced.matches && animationsEnabled()) this.classList.add('pj-motion-enabled');
      this.querySelectorAll('.pj-card').forEach(card => {
        const price = card.querySelector('.product-card__price');
        if (price && !price.querySelector('.pj-price-detail')) {
          const detail = document.createElement('span'); detail.className = 'pj-price-detail';
          detail.textContent = card.dataset.currency || '';
          price.append(detail);
        }
      });
      const motion = () => {
        this.reveals?.disconnect();
        const items = [...this.querySelectorAll('[data-pj-reveal]')];
        if (reduced.matches || !animationsEnabled() || !('IntersectionObserver' in window)) {
          this.classList.remove('pj-motion-enabled'); items.forEach(el => el.classList.add('pj-visible')); return;
        }
        this.classList.add('pj-motion-enabled');
        this.reveals = new IntersectionObserver(entries => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = entry.target, race = el.closest('[data-pj-race]');
            if (race && el.dataset.pjReveal === 'text' && !race.dataset.pjRevealStart) race.dataset.pjRevealStart = String(performance.now());
            if (['left','right'].includes(el.dataset.pjReveal)) {
              const elapsed = race ? performance.now() - Number(race.dataset.pjRevealStart || 0) : 1000;
              el.style.setProperty('--pj-delay', `${Math.max(0, 320 - elapsed) + (el.dataset.pjReveal === 'right' ? 160 : 60)}ms`);
            }
            el.classList.add('pj-visible'); this.reveals.unobserve(el);
          }
        }, { threshold: .08, rootMargin: '0px 0px -7% 0px' });
        items.forEach(el => this.reveals.observe(el));
      };
      document.fonts.ready.then(() => { if (this.isConnected) motion(); }); on(reduced, 'change', motion);
      on(document, 'shopify:section:load', motion);
      const updates = new Map(); this.carouselCleanup = [];
      this.resize = new ResizeObserver(entries => entries.forEach(e => updates.get(e.target)?.()));
      this.querySelectorAll('[data-pj-carousel]').forEach(carousel => {
        const track = carousel.querySelector('.pj-product-track'), prev = carousel.querySelector('.pj-prev'), next = carousel.querySelector('.pj-next'), cards = [...track.querySelectorAll('.pj-card')], status = carousel.querySelector('.pj-carousel-status');
        const looping = cards.length > 1, copies = 3;
        const tabindex = new Map();
        const rememberLinks = card => card.querySelectorAll('a,button,[tabindex]').forEach(link => tabindex.set(link, link.getAttribute('tabindex')));
        cards.forEach((card, index) => { card.dataset.pjProductIndex = index; rememberLinks(card); });
        if (looping) {
          const before = document.createDocumentFragment(), after = document.createDocumentFragment();
          const clone = card => {
            const copy = card.cloneNode(true); copy.dataset.pjLoopClone = '';
            [copy, ...copy.querySelectorAll('*')].forEach(node => {
              node.removeAttribute('id'); node.removeAttribute('data-shopify-editor-block');
              node.removeAttribute('data-shopify-editor-section');
            });
            rememberLinks(copy); return copy;
          };
          for (let copy = 0; copy < copies; copy++) cards.forEach(card => { before.append(clone(card)); after.append(clone(card)); });
          track.prepend(before); track.append(after); track.classList.add('pj-loop-track');
        }
        const allCards = [...track.querySelectorAll('.pj-card')];
        const step = () => cards.length > 1 ? cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left : cards[0]?.getBoundingClientRect().width || 1;
        const modulo = (value, length) => ((value % length) + length) % length;
        let lastStatus = '', drag, suppressClick = false, raf, animationFrame, settleTimer, animation, measuredStep = 0, measuredWidth = 0, currentIndex = 0;
        const origin = () => looping ? copies * cards.length * measuredStep : 0;
        const jump = left => track.scrollTo({left, behavior:'instant'});
        const stopAnimation = () => { cancelAnimationFrame(animationFrame); animation = null; track.classList.remove('pj-loop-moving'); };
        const normalise = () => {
          if (!looping || !measuredStep) return;
          const cycle = cards.length * measuredStep, left = track.scrollLeft;
          const equivalent = origin() + modulo(left - origin(), cycle), shift = equivalent - left;
          if (Math.abs(shift) < .5) return;
          const focused = document.activeElement, focusedCard = focused?.closest('.pj-card');
          const focusedLinkIndex = focusedCard && track.contains(focusedCard) ? [...focusedCard.querySelectorAll('a,button,[tabindex]')].indexOf(focused) : -1;
          track.classList.add('pj-loop-moving'); jump(equivalent);
          if (drag) drag.start += shift;
          if (animation) { animation.start += shift; animation.target += shift; }
          update();
          if (focusedLinkIndex >= 0) {
            const replacement = allCards.find(card => card.dataset.pjProductIndex === focusedCard.dataset.pjProductIndex && card.getAttribute('aria-hidden') !== 'true');
            replacement?.querySelectorAll('a,button,[tabindex]')[focusedLinkIndex]?.focus({preventScroll:true});
          }
          if (!animation && !drag?.moved) track.classList.remove('pj-loop-moving');
        };
        const update = () => {
          if (track.clientWidth && track.clientWidth !== measuredWidth) { layout(); return; }
          prev.disabled = next.disabled = !looping;
          if (!measuredStep) return;
          const first = modulo(Math.round((track.scrollLeft - origin()) / measuredStep), cards.length || 1);
          currentIndex = first;
          const gap = looping ? measuredStep - cards[0].getBoundingClientRect().width : 0;
          const visible = Math.min(cards.length, Math.max(1, Math.floor((track.clientWidth + gap) / measuredStep + .02)));
          const indices = Array.from({length:visible}, (_, index) => modulo(first + index, cards.length) + 1);
          const text = cards.length ? `Showing products ${indices.join(', ')} of ${cards.length}` : 'No products selected';
          if (text !== lastStatus) { status.textContent = text; lastStatus = text; }
          const trackRect = track.getBoundingClientRect(), exposed = new Set();
          allCards.forEach(card => {
            const rect = card.getBoundingClientRect(), index = card.dataset.pjProductIndex;
            const visible = rect.right > trackRect.left + 2 && rect.left < trackRect.right - 2 && !exposed.has(index);
            card.setAttribute('aria-hidden', String(!visible)); if (visible) exposed.add(index);
            card.querySelectorAll('a,button,[tabindex]').forEach(link => {
              const value = tabindex.get(link);
              if (!visible) link.setAttribute('tabindex', '-1');
              else if (value === null) link.removeAttribute('tabindex');
              else link.setAttribute('tabindex', value);
            });
          });
        };
        const animateTo = target => {
          stopAnimation(); clearTimeout(settleTimer);
          if (reduced.matches) { jump(target); normalise(); update(); return; }
          animation = {start:track.scrollLeft, target, time:performance.now()};
          track.classList.add('pj-loop-moving');
          const tick = now => {
            if (!animation) return;
            const progress = Math.min(1, (now - animation.time) / 320), eased = 1 - Math.pow(1 - progress, 3);
            jump(animation.start + (animation.target - animation.start) * eased);
            if (looping && (track.scrollLeft < measuredStep || track.scrollLeft > track.scrollWidth - track.clientWidth - measuredStep)) normalise();
            update();
            if (progress < 1) animationFrame = requestAnimationFrame(tick);
            else { animation = null; normalise(); track.classList.remove('pj-loop-moving'); update(); }
          };
          animationFrame = requestAnimationFrame(tick);
        };
        const move = dir => {
          if (!looping || !measuredStep) return;
          const from = animation?.target ?? track.scrollLeft;
          animateTo(origin() + (Math.round((from - origin()) / measuredStep) + dir) * measuredStep);
        };
        const settle = () => { if (!animation && !drag) { normalise(); update(); } };
        const layout = () => {
          const nextStep = step();
          if (!track.clientWidth || nextStep <= 0) return;
          measuredWidth = track.clientWidth;
          if (Math.abs(nextStep - measuredStep) > .01) {
            stopAnimation(); measuredStep = nextStep;
            jump(origin() + currentIndex * measuredStep);
          }
          update();
        };
        on(prev, 'click', () => move(-1)); on(next, 'click', () => move(1));
        on(track, 'scroll', () => {
          cancelAnimationFrame(raf); raf = requestAnimationFrame(() => {
            if (looping && !animation && (track.scrollLeft < measuredStep || track.scrollLeft > track.scrollWidth - track.clientWidth - measuredStep)) normalise();
            update();
          });
          clearTimeout(settleTimer); settleTimer = setTimeout(settle, 160);
        }, {passive:true});
        on(track, 'scrollend', settle);
        on(track, 'wheel', stopAnimation, {passive:true});
        on(track, 'touchstart', stopAnimation, {passive:true});
        updates.set(track, layout); this.resize.observe(track);
        on(track, 'keydown', event => {
          if (event.target !== track || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          event.preventDefault();
          if (['Home','End'].includes(event.key)) animateTo(origin() + (event.key === 'Home' ? 0 : Math.max(0, cards.length - 1) * measuredStep));
          else move(event.key === 'ArrowRight' ? 1 : -1);
        });
        on(track, 'pointerdown', event => { stopAnimation(); suppressClick = false; if (event.pointerType === 'mouse' && event.button === 0) drag = {id:event.pointerId,x:event.clientX,start:track.scrollLeft,moved:false}; });
        on(track, 'pointermove', event => {
          if (!drag) return; const delta = event.clientX - drag.x;
          if (Math.abs(delta) > 8) { drag.moved = true; track.classList.add('pj-dragging'); track.setPointerCapture(event.pointerId); event.preventDefault(); track.scrollLeft = drag.start - delta; }
        });
        const finishDrag = () => {
          if (!drag) return; const moved = drag.moved; suppressClick = moved; drag = null; track.classList.remove('pj-dragging');
          if (moved) animateTo(origin() + Math.round((track.scrollLeft - origin()) / measuredStep) * measuredStep); update();
        };
        on(track, 'pointerup', finishDrag); on(track, 'pointercancel', finishDrag);
        on(track, 'click', event => { if (suppressClick || drag?.moved) { event.preventDefault(); event.stopPropagation(); suppressClick = false; } }, {capture:true});
        this.carouselCleanup.push(() => {
          stopAnimation(); cancelAnimationFrame(raf); clearTimeout(settleTimer);
          track.querySelectorAll('[data-pj-loop-clone]').forEach(card => card.remove());
          cards.forEach(card => { card.removeAttribute('aria-hidden'); delete card.dataset.pjProductIndex; });
          tabindex.forEach((value, link) => { if (value === null) link.removeAttribute('tabindex'); else link.setAttribute('tabindex', value); });
          track.classList.remove('pj-loop-track', 'pj-dragging'); jump(0);
        });
        layout();
      });
      const nav = this.querySelector('.pj-race-nav');
      let updateNav = () => {};
      if (nav) {
        const multi = this.hasAttribute('data-pj-multi-section'), slot = this.querySelector('.pj-race-nav-slot');
        const raceNodes = () => [...(multi ? document : this).querySelectorAll('[data-pj-race]')];
        const refreshNavigation = () => {
          if (!this.isConnected) return;
          if (multi) {
            const races = raceNodes(), track = nav.querySelector('.pj-nav-track');
            const signature = races.map(r => `${r.id}:${r.dataset.pjNavLabel || r.querySelector('.pj-title')?.textContent || ''}`).join('|');
            if (signature !== this.navSignature) {
              this.navSignature = signature;
              const fragment = document.createDocumentFragment();
              races.forEach((race,index) => {
                const link = document.createElement('a'); link.className = 'pj-race-pill';
                link.href = `#${race.id}`; link.dataset.pjRaceLink = race.id;
                link.textContent = race.dataset.pjNavLabel || race.querySelector('.pj-title')?.textContent || 'Race day';
                link.setAttribute('aria-current', String(index === 0)); fragment.append(link);
              });
              track.replaceChildren(fragment);
            }
          }
          updateNav();
        };
        updateNav = () => {
          if (!this.isConnected) return;
          const navigationTrack = nav.querySelector('.pj-nav-track');
          const firstButton = navigationTrack?.firstElementChild, lastButton = navigationTrack?.lastElementChild;
          if (firstButton && lastButton) {
            const trackStyle = getComputedStyle(navigationTrack);
            const availableWidth = navigationTrack.clientWidth - parseFloat(trackStyle.paddingLeft) - parseFloat(trackStyle.paddingRight);
            const buttonRowWidth = lastButton.getBoundingClientRect().right - firstButton.getBoundingClientRect().left;
            nav.style.setProperty('--pj-nav-heading-width', `${Math.min(availableWidth, buttonRowWidth)}px`);
          }
          const height = nav.getBoundingClientRect().height, header = document.getElementById('site-header');
          const headerOffset = header && ['fixed','sticky'].includes(getComputedStyle(header).position) ? header.getBoundingClientRect().height : 0;
          const roots = multi ? document.querySelectorAll('pj-spring-carnival') : [this];
          roots.forEach(root => {root.style.setProperty('--pj-header-offset', `${headerOffset}px`);root.style.setProperty('--pj-nav-height', `${height}px`);});
          const races = raceNodes();
          if (multi && slot) {
            const slotRect = slot.getBoundingClientRect(); slot.style.height = `${height}px`;
            nav.style.setProperty('--pj-floating-left', `${slotRect.left}px`);
            nav.style.setProperty('--pj-floating-width', `${slotRect.width}px`);
            const modules = [...document.querySelectorAll('.pj-race,.pj-accessories')], last = modules[modules.length - 1];
            const floating = slotRect.top <= headerOffset && !!last && last.getBoundingClientRect().bottom > headerOffset + height;
            nav.classList.toggle('pj-floating', floating); nav.classList.toggle('pj-stuck', floating);
          } else nav.classList.toggle('pj-stuck', nav.getBoundingClientRect().top <= headerOffset);
          let current = races[0]; for (const race of races) if (race.getBoundingClientRect().top <= height + headerOffset + 120) current = race;
          nav.querySelectorAll('[data-pj-race-link]').forEach(a => a.setAttribute('aria-current', String(a.dataset.pjRaceLink === current?.id)));
        };
        updates.set(nav,updateNav); this.resize.observe(nav);
        on(window, 'scroll', () => {cancelAnimationFrame(this.scrollFrame);this.scrollFrame = requestAnimationFrame(updateNav);}, {passive:true});
        on(nav, 'click', event => {
          const link = event.target.closest('[data-pj-race-link]'); if (!link || !nav.contains(link)) return;
          const target = document.getElementById(link.dataset.pjRaceLink); if (!target) return;
          event.preventDefault(); target.scrollIntoView({behavior:reduced.matches ? 'instant' : 'smooth',block:'start'});
        });
        ['shopify:section:load','shopify:section:reorder'].forEach(event => on(document,event,refreshNavigation));
        on(document,'shopify:section:unload',() => {cancelAnimationFrame(this.scrollFrame);this.scrollFrame=requestAnimationFrame(refreshNavigation);});
        refreshNavigation();
        document.fonts.ready.then(() => { if (this.isConnected) updateNav(); });
      }
      this.videoObserver = new IntersectionObserver(entries => entries.forEach(entry => { const film = entry.target.querySelector('video'); if (!entry.isIntersecting && film && !film.paused) film.pause(); }));
      this.querySelectorAll('.pj-film').forEach(film => {
        const panel = film.closest('.pj-video-panel'), play = panel.querySelector('.pj-play'), error = panel.querySelector('.pj-film-error');
        const controls = panel.querySelector('.pj-film-controls'), toggle = controls.querySelector('.pj-film-toggle'), seek = controls.querySelector('.pj-film-seek'), mute = controls.querySelector('.pj-film-mute');
        const playLabel = play.getAttribute('aria-label'), filmLabel = playLabel.replace(/^Play /, '');
        film.controls = false;
        film.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback');
        film.setAttribute('disablepictureinpicture', ''); film.setAttribute('disableremoteplayback', ''); film.setAttribute('x-webkit-airplay', 'deny');
        const clock = seconds => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
        const syncControls = () => {
          toggle.dataset.paused = String(film.paused); toggle.setAttribute('aria-label', `${film.paused ? 'Play' : 'Pause'} ${filmLabel}`);
          mute.setAttribute('aria-pressed', String(film.muted)); mute.setAttribute('aria-label', `${film.muted ? 'Unmute' : 'Mute'} ${filmLabel}`);
          const ready = Number.isFinite(film.duration) && film.duration > 0; seek.disabled = !ready;
          if (ready) { seek.value = String(Math.round(film.currentTime / film.duration * 1000)); seek.setAttribute('aria-valuetext', `${clock(film.currentTime)} of ${clock(film.duration)}`); }
        };
        const start = async () => {
          error.hidden = true; play.hidden = true; controls.hidden = false;
          try { await film.play(); }
          catch { controls.hidden = true; play.hidden = false; error.hidden = false; }
          syncControls();
        };
        on(play, 'click', start);
        on(toggle, 'click', () => { if (film.paused) start(); else film.pause(); });
        on(mute, 'click', () => {film.muted = !film.muted;});
        on(seek, 'input', () => {if (Number.isFinite(film.duration) && film.duration > 0) film.currentTime = Number(seek.value) / 1000 * film.duration;});
        on(film, 'play', () => { play.hidden = true; controls.hidden = false; syncControls(); document.querySelectorAll('pj-spring-carnival video').forEach(other => {if (other !== film) other.pause();}); });
        ['pause','timeupdate','durationchange','loadedmetadata','volumechange'].forEach(event => on(film, event, syncControls));
        on(film, 'error', () => {controls.hidden = true; play.hidden = false; error.hidden = false;});
        play.hidden = !film.paused; controls.hidden = film.paused; syncControls(); this.videoObserver.observe(panel);
      });
      on(document, 'shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const module = event.target.closest('.pj-race,.pj-accessories') || event.target;
        module.querySelectorAll('[data-pj-reveal]').forEach(el => el.classList.add('pj-visible'));
        const card = event.target.closest('.pj-card'); if (card) card.scrollIntoView({block:'nearest',inline:'nearest',behavior:'instant'});
      });
      updateNav();
    }
  }
  customElements.define('pj-spring-carnival', PJSpringCarnival);
})();
